import { FC, useMemo } from 'react';
import {
  AdvancedPagination,
  Card,
  Divider,
  RadixTable,
  TColumn,
} from '@features/ui';
import { differenceDate, formatDate } from '@lib/utils.ts';
import { TPendingMerchant } from '@src/types';
import { Pagination } from '@src/types/common';

type PendingMerchantsProps = {
  data: TPendingMerchant[];
  pagination: Pagination;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
};

export const PendingMerchants: FC<PendingMerchantsProps> = ({
  data,
  pagination,
  onPageChange,
  onPageSizeChange,
}) => {
  const columns = useMemo(
    (): TColumn<TPendingMerchant>[] => [
      {
        render: (item) => (
          <div className={`pr-1`}>
            <p className={'text-xs'}>{item?.user?.username ?? ''}</p>
            <p className={'text-xs'}>
              {item?.user?.first_name ?? ''} {item?.user?.last_name ?? ''}
            </p>
          </div>
        ),
        title: 'Name',
      },
      {
        title: 'Product',
        render: (item) => (
          <div className={'pr-1'}>
            <p>{item?.product_name ?? ''}</p>
          </div>
        ),
      },
      {
        title: 'Updated On',
        render: (item) => (
          <div className={'pr-1'}>
            <p>{formatDate(item?.updated_at)}</p>
          </div>
        ),
      },
      {
        title: 'Expires',
        render: (item) => {
          const expires = differenceDate(item?.created_at);

          if (!expires) {
            return (
              <div className={'min-w-[80px] pr-1'}>
                <p>{formatDate(item?.created_at)}</p>
              </div>
            );
          }

          return (
            <div className={'min-w-[80px] pr-1 text-[#C13839]'}>
              <p>{`${expires} Day!`}</p>
              <p>{formatDate(item?.created_at)}</p>
            </div>
          );
        },
      },
      {
        title: 'Product URL',
        render: (item) => (
          <div className={'pr-1 text-blue'}>
            <p>{item?.product_url ?? 'N/A'}</p>
          </div>
        ),
      },
      {
        title: 'Status',
        render: (item) => (
          <div className={'pr-1'}>
            <p>{item?.status ?? 'Awaiting Customer Completion'}</p>
          </div>
        ),
      },
    ],
    []
  );

  return (
    <Card className={'flex flex-col gap-y-5 items-end'}>
      <RadixTable columns={columns} data={data} />
      <Divider className={'w-full'} />
      <AdvancedPagination
        currentPage={pagination.currentPage}
        pageSize={pagination.pageSize}
        totalItems={pagination.totalItems}
        totalPages={pagination.totalPages}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
      />
    </Card>
  );
};
